/**
 * The icon set.
 *
 * The app uses Ionicons and its semantics are load-bearing — `flame` is always
 * a butter lamp, `flower` is always the lotus/blessing mark, `sparkles` is
 * always "confirmed". Rather than pull a whole icon package into the web
 * bundle, the same glyphs are redrawn here as inline SVG with the meanings
 * kept identical, so a flame means the same thing on both platforms.
 *
 * Stroke icons inherit `currentColor` and scale from one `size` prop. Solid
 * variants exist only where the app uses a filled glyph to express state
 * (a lit lamp, a saved monastery).
 */

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const PATHS = {
  /* — brand and offering — */
  flame: <path d="M12 3c.5 3-2 4-2 7a2 2 0 0 0 4 0c0-1 .6-1.7 1.2-2.3A7 7 0 0 1 12 21a7 7 0 0 1-3.4-13C10.2 6.6 12 5.6 12 3Z" />,
  lotus: (
    <>
      <path d="M12 20c-3.5 0-6.5-2.2-7.5-5.4 1.6-1 3.5-1 5 .2" />
      <path d="M12 20c3.5 0 6.5-2.2 7.5-5.4-1.6-1-3.5-1-5 .2" />
      <path d="M12 20c-2.2-1.6-3.4-4-3.4-6.6 0-2.6 1.2-5 3.4-6.6 2.2 1.6 3.4 4 3.4 6.6 0 2.6-1.2 5-3.4 6.6Z" />
    </>
  ),
  sparkles: (
    <>
      <path d="M12 3.5 13.6 8 18 9.5 13.6 11 12 15.5 10.4 11 6 9.5 10.4 8 12 3.5Z" />
      <path d="M18.5 14.5 19.3 17l2.2.8-2.2.8-.8 2.4-.8-2.4-2.2-.8 2.2-.8.8-2.5Z" />
    </>
  ),
  wheel: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3.5v5M12 15.5v5M3.5 12h5M15.5 12h5M6 6l3.5 3.5M14.5 14.5 18 18M18 6l-3.5 3.5M9.5 14.5 6 18" />
    </>
  ),
  /* — navigation — */
  compass: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m14.8 9.2-1.6 4.3-4.3 1.6 1.6-4.3 4.3-1.6Z" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m16 16 4.5 4.5" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6 6 18" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  chevronRight: <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />,
  chevronLeft: <path d="M14.5 5.5 8 12l6.5 6.5" />,
  chevronDown: <path d="m5.5 9.5 6.5 6.5 6.5-6.5" />,
  arrowRight: <path d="M4.5 12h15m-6-6 6 6-6 6" />,
  arrowLeft: <path d="M19.5 12h-15m6-6-6 6 6 6" />,
  external: <path d="M14 4.5h5.5V10M19 5l-8 8M18 14v4.5A1.5 1.5 0 0 1 16.5 20h-11A1.5 1.5 0 0 1 4 18.5v-11A1.5 1.5 0 0 1 5.5 6H10" />,
  /* — state — */
  heart: <path d="M12 20s-7.5-4.6-7.5-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20Z" />,
  bookmark: <path d="M6.5 4.5h11v15L12 15.6 6.5 19.5v-15Z" />,
  check: <path d="m5 12.5 4.5 4.5L19 7" />,
  checkCircle: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4.5 21 20H3l9-15.5Z" />
      <path d="M12 10v4M12 17h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5M12 7.8h.01" />
    </>
  ),
  shield: <path d="M12 3.5 19 6v6c0 4-3 7-7 8.5C8 19 5 16 5 12V6l7-2.5Z" />,
  camera: (
    <>
      <path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1-2h7l1 2h2A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-9Z" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  /* — content — */
  location: (
    <>
      <path d="M12 21s6.5-5.6 6.5-10a6.5 6.5 0 1 0-13 0c0 4.4 6.5 10 6.5 10Z" />
      <circle cx="12" cy="11" r="2.5" />
    </>
  ),
  time: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.2l3.2 2" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="15" rx="2" />
      <path d="M4 10h16M9 3.5v4M15 3.5v4" />
    </>
  ),
  journal: (
    <>
      <path d="M6 4.5h11a1.5 1.5 0 0 1 1.5 1.5v13.5H6a1.5 1.5 0 0 1-1.5-1.5V6A1.5 1.5 0 0 1 6 4.5Z" />
      <path d="M9 4.5v15" />
    </>
  ),
  quill: <path d="M19.5 4.5c-8 1-12 5-13.5 10L4 20m2-5.5c5-1.5 9-5.5 10-11m-6.5 12h5" />,
  video: (
    <>
      <rect x="3" y="6" width="12.5" height="12" rx="2.5" />
      <path d="m15.5 11 5-3v8l-5-3" />
    </>
  ),
  play: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M10.2 8.8 15.5 12l-5.3 3.2V8.8Z" />
    </>
  ),
  receipt: (
    <>
      <path d="M6 3.5h12v17l-2.5-1.6-2.5 1.6-2.5-1.6L8 20.5l-2-1.4v-15.6Z" />
      <path d="M9.5 8.5h5M9.5 12.5h5" />
    </>
  ),
  wallet: (
    <>
      <rect x="3.5" y="6.5" width="17" height="12" rx="2.5" />
      <path d="M3.5 10.5h17M16.5 14.5h1.5" />
    </>
  ),
  people: (
    <>
      <circle cx="9.5" cy="8.5" r="3" />
      <path d="M3.5 19.5a6 6 0 0 1 12 0M16 6.2a3 3 0 0 1 0 5.6M17 14.2a5.5 5.5 0 0 1 3.5 5.3" />
    </>
  ),
  book: <path d="M4 5.5A2 2 0 0 1 6 4h5v15H6a2 2 0 0 0-2 1.5v-15Zm16 0A2 2 0 0 0 18 4h-5v15h5a2 2 0 0 1 2 1.5v-15Z" />,
  infinite: <path d="M8 9a3 3 0 1 0 0 6c2.5 0 3-6 8-6a3 3 0 0 1 0 6c-5 0-5.5-6-8-6Z" />,
  language: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.6 12h16.8M12 3.6c2.2 2.3 3.4 5.3 3.4 8.4S14.2 18.1 12 20.4c-2.2-2.3-3.4-5.3-3.4-8.4S9.8 5.9 12 3.6Z" />
    </>
  ),
  navigate: <path d="M20 4 4 10.5l7 2.5 2.5 7L20 4Z" />,
  mail: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
      <path d="m4 7 8 6 8-6" />
    </>
  ),
  phone: <path d="M7 3.5 9.5 8 7.8 10a11 11 0 0 0 5.2 5.2l2-1.7 4.5 2.5-.6 3A2 2 0 0 1 16.7 21C9.7 20.3 3.7 14.3 3 7.3A2 2 0 0 1 4.9 5l2.1-1.5Z" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.6 9.5h16.8M3.6 14.5h16.8M12 3.6c2.4 2.5 3.6 5.4 3.6 8.4s-1.2 5.9-3.6 8.4c-2.4-2.5-3.6-5.4-3.6-8.4S9.6 6.1 12 3.6Z" />
    </>
  ),
  options: <path d="M4 7h9m3 0h4M4 17h4m3 0h9M14 4.5v5M9 14.5v5" />,
  filter: <path d="M4 6h16l-6 7v5l-4 2v-7L4 6Z" />,
  share: <path d="M8.6 13.4 15.4 17M15.4 7 8.6 10.6M18 6.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM9 12a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Zm9 5.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Z" />,
  /* The one brand mark in the set — always solid (see SOLID below), never a
     stroked outline, matching how the source mark itself is always drawn. */
  facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3Z" />,
};

/** Glyphs the app draws filled to express a state rather than a category. */
const SOLID = new Set(['flame', 'heart', 'bookmark', 'sparkles', 'check', 'facebook']);

export default function Icon({ name, size = 20, solid = false, className = undefined, style = undefined }) {
  const glyph = PATHS[name];
  if (!glyph) return null;
  const filled = solid && SOLID.has(name);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={className}
      style={style}
      aria-hidden="true"
      focusable="false"
      {...(filled
        ? { fill: 'currentColor', stroke: 'currentColor', strokeWidth: 1.2, strokeLinejoin: 'round' }
        : STROKE)}
    >
      {glyph}
    </svg>
  );
}
